import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Star, MapPin, Utensils, Heart, ExternalLink, MessageSquare } from "lucide-react";

const RestaurantList = ({ restaurants, wishlistIds, loading, onSelect, onToggleWishlist, onShowReviews }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (restaurants.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-12 text-center">
          <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t('restaurants.noRestaurants')}</p>
          <p className="text-sm text-muted-foreground mt-2">{t('restaurants.allVisited')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
      {restaurants.map((restaurant) => (
        <Card
          key={restaurant.restaurant_id}
          className="bg-card border-border card-hover cursor-pointer"
          onClick={() => onSelect(restaurant)}
          data-testid={`restaurant-card-${restaurant.restaurant_id}`}
        >
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-semibold mb-1">{restaurant.name}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {restaurant.location}, {restaurant.country}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {restaurant.cuisine_type && (
                    <span className="text-xs text-primary">{restaurant.cuisine_type}</span>
                  )}
                  {restaurant.price_range && (
                    <span className="text-xs text-amber-400">
                      {"€".repeat(restaurant.price_range)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <a
                  href={restaurant.reservation_url || `https://www.google.com/search?q=${encodeURIComponent(`${restaurant.name} ${restaurant.location || ""} reservieren`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`p-1 hover:scale-110 transition-transform ${restaurant.reservation_url ? 'text-green-500 hover:text-green-400' : 'text-muted-foreground hover:text-primary'}`}
                  title={restaurant.reservation_url ? "Direkt reservieren" : "Reservierung suchen"}
                  data-testid={`reserve-btn-${restaurant.restaurant_id}`}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button
                  onClick={(e) => { e.stopPropagation(); onShowReviews?.(restaurant); }}
                  className="p-1 hover:scale-110 transition-transform text-muted-foreground hover:text-primary"
                  title={t('reviews.title')}
                  data-testid={`reviews-btn-${restaurant.restaurant_id}`}
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => onToggleWishlist(restaurant.restaurant_id, e)}
                  className="p-1 hover:scale-110 transition-transform"
                  title={wishlistIds.has(restaurant.restaurant_id) ? t('wishlist.removeFromWishlist') : t('wishlist.addToWishlist')}
                  data-testid={`wishlist-btn-${restaurant.restaurant_id}`}
                >
                  <Heart
                    className={`w-5 h-5 ${wishlistIds.has(restaurant.restaurant_id) ? 'text-red-500 fill-red-500' : 'text-muted-foreground hover:text-red-400'}`}
                  />
                </button>
                <div className="flex gap-0.5">
                  {[...Array(restaurant.stars)].map((_, i) => (
                    <Star key={`star-${restaurant.restaurant_id}-${i}`} className="w-4 h-4 text-primary fill-primary" />
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default RestaurantList;
